import React, { useEffect, useMemo, useCallback, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Users, DollarSign, Shield, Calendar, Sparkles, Gem, Crown, History, Settings as SettingsIcon, Eye, Share2, AlertTriangle } from "lucide-react";
import AdminTeamPanel from "../components/AdminTeamPanel";
import AdminActivityPanel from "../components/AdminActivityPanel";
import AdminConcernsPanel from "../components/AdminConcernsPanel";
import OwnerSettingsPanel from "../components/OwnerSettingsPanel";
import PasswordAgeReminder from "../components/PasswordAgeReminder";
import PreviewInsightsPanel from "../components/PreviewInsightsPanel";
import ReferralActivityPanel from "../components/ReferralActivityPanel";

export default function AdminDashboard(){
  const { adminPerms, hasPermission, isOwner } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [trust, setTrust] = useState({ counts: {}, providers: [] });
  const [tab, setTab] = useState("verifications");

  const providerUsers = useMemo(() => users.filter(u => u.role === "provider"), [users]);

  const load = useCallback(() => {
    api.get("/admin/stats").then(r=>setStats(r.data)).catch(()=>{});
    api.get("/admin/users").then(r=>setUsers(Array.isArray(r.data) ? r.data : [])).catch(()=>setUsers([]));
    api.get("/admin/payments").then(r=>setPayments(Array.isArray(r.data) ? r.data : [])).catch(()=>setPayments([]));
    api.get("/admin/bookings").then(r=>setBookings(Array.isArray(r.data) ? r.data : [])).catch(()=>setBookings([]));
    api.get("/admin/membership").then(r=>setMemberships(Array.isArray(r.data) ? r.data : [])).catch(()=>setMemberships([]));
    api.get("/admin/trust").then(r=>setTrust(r.data || { counts: {}, providers: [] })).catch(()=>setTrust({ counts: {}, providers: [] }));
  }, []);
  useEffect(()=>{ load(); },[load]);

  const verify = async (id, action) => { await api.post(`/admin/verify/${id}?action=${action}`); load(); };
  const suspend = async (id) => { await api.post(`/admin/users/${id}/suspend`); load(); };

  // Permission-gated tabs
  const ownerView = isOwner();
  const canManageTeam = hasPermission("manage_admins");          // Owner-only
  const canManageSettings = hasPermission("company_settings");   // Owner-only
  // Tab order is intentional — most decision-critical tabs first (review →
  // insights → revenue), supporting tools after, settings last. Activity tab
  // is always visible to admins (backend filters their view).
  const tabs = [
    { key: "verifications", label: "Verifications",  show: true },                      // 1. Items needing review
    { key: "concerns",      label: "Concerns",       show: true,             icon: AlertTriangle }, // 2. Customer-reported issues
    { key: "preview",       label: "Preview Insights", show: canManageSettings, icon: Eye }, // 3. Funnel visibility (Owner)
    { key: "bookings",      label: "Bookings",       show: true },                      // 3. Revenue activity
    { key: "payments",      label: "Payments",       show: true },
    { key: "trust",         label: "Trust",          show: true },                      // 4. Concierge trust status
    { key: "users",         label: "Users",          show: true },
    { key: "membership",    label: "Membership",     show: true },
    { key: "referrals",     label: "Referrals",      show: canManageSettings, icon: Share2 },
    { key: "team",          label: "Team & Invites", show: canManageTeam,    icon: Crown },     // 5. Team
    { key: "activity",      label: "Activity",       show: true,             icon: History },    // 6. Logs
    { key: "settings",      label: "Owner Settings", show: canManageSettings, icon: SettingsIcon }, // 7. Settings last
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 pb-28 lg:pb-10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Admin Command Center</h1>
          <p className="text-[#071A2D]/70 mt-1">Operate the TREL marketplace.</p>
        </div>
        {adminPerms && (
          <div
            data-testid="admin-role-badge"
            className={`text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full font-semibold flex items-center gap-1.5 ${
              ownerView
                ? "bg-[#C9A45C] text-[#071A2D]"
                : "bg-[#064E3B] text-[#FFF8EE]"
            }`}
          >
            {ownerView && <Crown size={12}/>}
            {ownerView ? "Founder Owner" : "Admin"}
          </div>
        )}
      </div>

      <PasswordAgeReminder/>

      {stats && (
        <div className="grid md:grid-cols-4 gap-4 mt-8">
          <Stat icon={Users} label="Users" value={stats.users}/>
          <Stat icon={Shield} label="Verified" value={stats.verified_providers}/>
          <Stat icon={Calendar} label="Bookings" value={stats.bookings}/>
          <Stat icon={DollarSign} label="GMV" value={`$${(stats.gross_volume ?? 0).toFixed(0)}`}/>
        </div>
      )}
      {stats && (
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <Stat icon={DollarSign} label="TREL Revenue (20%)" value={`$${(stats.trel_revenue ?? 0).toFixed(0)}`} accent/>
          <Stat icon={DollarSign} label="Provider Payouts (80%)" value={`$${(stats.payouts ?? 0).toFixed(0)}`}/>
          <Stat icon={Users} label="Hosts" value={stats.hosts}/>
        </div>
      )}
      {stats && (
        <div className="grid md:grid-cols-4 gap-4 mt-4">
          <Stat icon={Sparkles} label="TREL+ Providers" value={stats.trel_plus_providers || 0}/>
          <Stat icon={DollarSign} label="MRR (TREL+)" value={`$${(stats.mrr || 0).toFixed(0)}`} accent/>
          <Stat icon={DollarSign} label="Host Commissions" value={`$${(stats.host_commissions || 0).toFixed(0)}`}/>
          <Stat icon={DollarSign} label="Tips Processed" value={`$${(stats.tips_processed || 0).toFixed(0)}`}/>
        </div>
      )}
      {stats && (
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <Stat icon={Shield} label="Identity Verified" value={stats.identity_verified_providers || 0}/>
          <Stat icon={Gem} label="TREL Elite Concierges" value={stats.elite_concierges || 0} accent/>
          <Stat icon={DollarSign} label="Verification Revenue" value={`$${(stats.verification_fees_revenue || 0).toFixed(0)}`}/>
        </div>
      )}

      <div className="mt-10 flex gap-2 flex-wrap">
        {tabs.filter(t => t.show).map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={()=>setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-4 py-2 rounded-full text-sm flex items-center gap-1.5 ${
                tab===t.key
                  ? "bg-[#064E3B] text-[#FFF8EE]"
                  : "card-luxury"
              }`}
            >
              {Icon && <Icon size={13}/>}
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        {tab==="users" && (
          <div className="space-y-2">
            {users.map(u=>(
              <div key={u.user_id} className="p-4 rounded-2xl card-luxury flex items-center gap-4">
                <img src={u.profile_photo} className="w-10 h-10 rounded-full" alt=""/>
                <div className="flex-1"><div className="font-medium">{u.name}</div><div className="text-xs text-[#071A2D]/60">{u.email} · {u.role}</div></div>
                <span data-testid={`user-lang-${u.user_id}`} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#064E3B]/10 text-[#064E3B] font-semibold">{(u.preferred_language || "en").toUpperCase()}</span>
                <span className="text-xs px-2 py-1 rounded-full bg-[#064E3B] text-[#FFF8EE]">{u.account_status}</span>
                <button onClick={()=>suspend(u.user_id)} className="text-xs underline text-[#FF7A45]">Suspend</button>
              </div>
            ))}
          </div>
        )}
        {tab==="payments" && (
          <div className="space-y-2">
            {payments.map(p=>(
              <div key={p.payment_id} className="p-4 rounded-2xl card-luxury flex items-center gap-4">
                <div className="flex-1 font-mono text-sm">{p.stripe_ref}</div>
                <div className="text-sm">${p.total}</div>
                <div className="text-sm text-[#C9A45C]">TREL ${p.trel_fee}</div>
                <div className="text-sm text-[#064E3B]">Provider ${p.provider_payout}</div>
                <span className="text-xs px-2 py-1 rounded-full bg-[#064E3B] text-[#FFF8EE]">{p.status}</span>
              </div>
            ))}
            {payments.length===0 && <div className="p-8 text-center text-[#071A2D]/60">No payments yet.</div>}
          </div>
        )}
        {tab==="bookings" && (
          <div className="space-y-2">
            {bookings.map(b=>(
              <div key={b.booking_id} className="p-4 rounded-2xl card-luxury flex items-center gap-4">
                <div className="flex-1 text-sm">{b.experience_id} · {b.date}</div>
                <div className="text-sm">${b.amount}</div>
                <span className="text-xs px-2 py-1 rounded-full bg-[#064E3B] text-[#FFF8EE]">{b.status}</span>
              </div>
            ))}
          </div>
        )}
        {tab==="verifications" && (
          <div className="space-y-2">
            {providerUsers.map(u=>(
              <div key={u.user_id} className="p-4 rounded-2xl card-luxury flex items-center gap-4">
                <img src={u.profile_photo} className="w-10 h-10 rounded-full" alt=""/>
                <div className="flex-1"><div className="font-medium">{u.name}</div><div className="text-xs text-[#071A2D]/60">{u.email}</div></div>
                <button onClick={()=>verify(u.user_id,"approve")} className="btn-emerald text-xs px-4 py-1.5">Approve</button>
                <button onClick={()=>verify(u.user_id,"reject")} className="btn-outline-emerald text-xs px-4 py-1.5">Reject</button>
              </div>
            ))}
          </div>
        )}
        {tab==="membership" && (
          <div className="space-y-2">
            {memberships.length === 0 && <div className="p-8 text-center text-[#071A2D]/60">No TREL+ subscriptions yet.</div>}
            {memberships.map(m=>(
              <div key={m.subscription_id} data-testid={`membership-${m.subscription_id}`} className="p-4 rounded-2xl card-luxury flex items-center gap-4">
                <Sparkles className="text-[#C9A45C]" size={18}/>
                <div className="flex-1">
                  <div className="font-medium">{m.provider?.name}</div>
                  <div className="text-xs text-[#071A2D]/60">{m.provider?.email} · {m.plan}</div>
                </div>
                <div className="text-sm font-mono">${m.amount}/mo</div>
                <span className={`text-xs px-2 py-1 rounded-full ${m.status==="active"?"bg-[#064E3B] text-[#FFF8EE]":"bg-[#FF7A45] text-[#FFF8EE]"}`}>{m.status}</span>
              </div>
            ))}
          </div>
        )}
        {tab==="trust" && (
          <div data-testid="admin-trust-panel">
            <div className="grid sm:grid-cols-4 gap-3 mb-4">
              <TrustStat label="Identity Verified" value={trust.counts?.identity_verified || 0} color="#00AEEF"/>
              <TrustStat label="TREL Certified" value={trust.counts?.trel_certified || 0} color="#064E3B"/>
              <TrustStat label="TREL Elite" value={trust.counts?.elite || 0} color="#C9A45C"/>
              <TrustStat label="Pending Screening" value={trust.counts?.elite_pending_screening || 0} color="#FF7A45"/>
            </div>
            <div className="space-y-2">
              {(trust.providers || []).map(p => (
                <div key={p.user_id} className="p-4 rounded-2xl card-luxury flex items-center gap-3 flex-wrap" data-testid={`trust-row-${p.user_id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-[#071A2D]/60">{p.email} · {p.city}, {p.state}</div>
                  </div>
                  <StatusPill label="Identity" status={p.identity_verified ? "Verified" : (p.identity_status || "Not Started")} done={p.identity_verified} color="#00AEEF"/>
                  <StatusPill label="Certified" status={p.trel_certified ? "Yes" : "No"} done={p.trel_certified} color="#064E3B"/>
                  <StatusPill label="Payment" status={p.elite_payment_status || "Not Started"} done={p.elite_payment_status === "Paid"} color="#FF7A45"/>
                  <StatusPill label="Screening" status={p.background_status || "Not Started"} done={p.background_status === "Approved"} color="#C9A45C"/>
                  <StatusPill label="Elite" status={p.elite_badge_active ? "Active" : "Off"} done={p.elite_badge_active} color="#C9A45C"/>
                </div>
              ))}
              {(trust.providers || []).length === 0 && <div className="p-8 text-center text-[#071A2D]/60">No providers yet.</div>}
            </div>
            <div className="text-[11px] text-[#071A2D]/55 mt-4">
              TREL admin view never shows sensitive PII — only status flags from Stripe Identity &amp; Checkr.
            </div>
          </div>
        )}
        {tab==="team" && canManageTeam && <AdminTeamPanel/>}
        {tab==="concerns" && <AdminConcernsPanel/>}
        {tab==="activity" && <AdminActivityPanel/>}
        {tab==="settings" && canManageSettings && <OwnerSettingsPanel/>}
        {tab==="preview" && canManageSettings && <PreviewInsightsPanel/>}
        {tab==="referrals" && canManageSettings && <ReferralActivityPanel/>}
      </div>
    </div>
  );
}

const Stat = ({icon:I,label,value,accent}) => (
  <div className={`p-5 rounded-2xl ${accent?"bg-[#064E3B] text-[#FFF8EE] border border-transparent":"card-luxury"}`}>
    <I size={18}/>
    <div className="text-xs uppercase tracking-widest opacity-70 mt-3">{label}</div>
    <div className="text-2xl font-serif mt-1">{value}</div>
  </div>
);

const TrustStat = ({ label, value, color }) => (
  <div className="p-4 rounded-2xl bg-[#FFF8EE] border" style={{ borderColor: `${color}33` }}>
    <div className="text-[10px] uppercase tracking-widest" style={{ color }}>{label}</div>
    <div className="text-2xl font-serif mt-1">{value}</div>
  </div>
);

const StatusPill = ({ label, status, done, color }) => (
  <span
    className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"
    style={{
      background: done ? color : `${color}1A`,
      color: done ? "#FFF8EE" : color,
    }}
    title={`${label}: ${status}`}
  >
    {label}: {status}
  </span>
);
